import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import usePolicies from '@verzekeringapp-mono/shared/src/hooks/use_policies'
import { getPolicyById } from '@verzekeringapp-mono/shared/src/state/selectors/policies'
import { addYears, format } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import Button from '~/components/Button'
import { useNavigate } from 'react-router-dom'
import dateUtils from '@verzekeringapp-mono/shared/src/utils/date_utils'
import useValueMeterForms from '@verzekeringapp-mono/shared/src/hooks/use_value_meter_forms'
import { getRebuildValueMeterForm as getRebuildValueMeterFormSelector } from '@verzekeringapp-mono/shared/src/state/selectors/value_meter_forms'
import { ValueMeterFormQuestions } from './components/ValueMeterFormQuestions'
import { getPendingRelationMutations } from '@verzekeringapp-mono/shared/src/state/selectors/relation_mutations'
import { buildFormSubmissionPayload } from '@verzekeringapp-mono/shared/src/utils/form_builder_utils'

const RebuildValueMeterForm = () => {
  const { id } = useParams<{
    id: string
  }>()

  const navigate = useNavigate()
  const { postRebuildValueMeter } = usePolicies()
  const idNotNull = id ? id : ''
  const policy = useAppSelector(getPolicyById(idNotNull))
  const [error, setError] = useState<string | null>(null)
  const valueMeterIsLoading = useAppSelector(
    state => state.policies.isLoadingValueMeter
  )
  const { getRebuildValueMeterForm } = useValueMeterForms()
  const pendingRelationMutations = useAppSelector(getPendingRelationMutations)
  const valueMeterIsPending = useMemo(() => pendingRelationMutations?.activePolicyChanges.includes(policy ? Number(policy.policyNumber) : -1), [pendingRelationMutations, policy])

  const questions = useAppSelector(getRebuildValueMeterFormSelector)

  useEffect(() => {
    getRebuildValueMeterForm()
  }, [])

  const underInsuranceGuaranteeDate = policy?.details?.find(
    detail => detail.name === 'underinsurance_guarantee_date'
  )?.code

  const expireDate = underInsuranceGuaranteeDate
    ? addYears(dateUtils.dutchDateToJSDate(underInsuranceGuaranteeDate), 5)
    : null

  const rebuildValue = useMemo(() => policy?.details?.find(
    detail => detail.name === 'rebuilding_value'
  )?.value, [policy])

  const form = useForm<Record<string, string>>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      rebuild_value: (rebuildValue || '') as string
    }
  })
  const {
    formState: { isValid }
  } = form

  const showConfirmPostValueMeter = useCallback(() => {
    const confirm = window.confirm(
      'De ingevulde waardemeter wordt zo spoedig mogelijk door je adviseur in behandeling genomen.'
    )

    if (!confirm) {
      return
    }

    navigate('/dossier/waardemeters')
  }, [navigate])

  return (
    <div className='w-full rounded-lg bg-surface-page p-8'>
      <div className='w-full lg:w-2/3'>
        <div>
          <p>
            {expireDate &&
              `Je garantie tegen onderverzekering verloopt op ${format(
                expireDate,
                'dd-MM-yyyy'
              )}. Heb je je huis verbouwd?`}{' '}
            Vul onderstaand formulier in als de verbouwingskosten hoger waren
            dan € 5.000,- en de inhoud (m3) van je woning groter is geworden
            door de verbouwing. Is dit niet het geval, dan hoef je het formulier
            niet in te vullen.
          </p>
        </div>

        <FormProvider {...form}>
          <form
            className='flex flex-col my-4'
            onSubmit={form.handleSubmit(data => {
              if (valueMeterIsPending) {
                return
              }

              if (!policy?.policyNumber || !questions) {
                setError(
                  'Kan waardemeter niet indienen, ververs de pagina en probeer het opnieuw.'
                )

                return
              }

              setError(null)

              const formSubmissionPayload = buildFormSubmissionPayload(data, questions)
              const result = Object.fromEntries(
                Object.keys(formSubmissionPayload).map(key => [key, formSubmissionPayload[key]])
              )

              postRebuildValueMeter(
                policy?.policyNumber,
                result
              )
                .then(() => {
                  showConfirmPostValueMeter()
                })
                .catch(e => {
                  setError(e.message)
                })
            })}
          >
            {questions && (
              <ValueMeterFormQuestions
                questions={questions}
                setValue={form.setValue}
                watch={form.watch}
                control={form.control}
                disabled={valueMeterIsPending}
              />
            )}

            {error && <p className='mb-4 text-sm text-status-danger-foreground'>{error}</p>}

            {
              valueMeterIsPending && (
                <p className='mb-4 text-text-muted'>
                  Er is al een wijzigingsverzoek op deze polis. Neem contact op met je adviseur voor het verwerken van je waardemeter.
                </p>
              )
            }

            <Button
              type='submit'
              className='primary-btn w-full'
              disabled={!isValid || valueMeterIsPending}
              isLoading={valueMeterIsLoading}
            >
              {valueMeterIsPending ? 'Waardemeter in behandeling' : 'Waardemeter indienen'}
            </Button>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default RebuildValueMeterForm
